import { useEffect, useMemo, useState } from "react";
import { Field, reduxForm, formValueSelector, change } from "redux-form";
import { connect } from "react-redux";
import { Typography, Box, Grid, Button } from "@material-ui/core";
import { TextInput, DateTimeInput } from "../forms/FormInputs";
import { useMetricsState } from "../../context/metrics";

// Field groups to match PWA layout
const FIELD_GROUPS = {
  pH: ["ag_ph_in", "ag_ph_out", "ag_ph_bag"],
  EC: ["ag_ec_in", "ag_ec_out", "ag_ec_bag"],
  "Run-off": ["ag_ml_in", "ag_ml_out", "ag_run_off"],
  Moisture: ["ag_moisture", "ag_water_cubes"],
  Targets: [
    "ag_irrigations_per_day",
    "ag_total_run_time",
    "ag_litres_per_day",
    "ag_drain_target",
    "ag_ph_target",
    "ag_ec_target",
  ],
};

// Flat order for sorting
const FIELD_ORDER = Object.values(FIELD_GROUPS).flat();

// Helper to get run-off multiplication factor from field (matches PWA)
const getRunOffMultFactor = (field) => {
  const factor = parseFloat(field?.runoff_multiplication_factor ?? "1");
  if (isNaN(factor) || factor === 0) return 1;
  return factor;
};

var decimalRegex = /^-?(\d+\.?\d*|\d*\.?\d+)$/;
// THIS NEEDS TO REFERENCE METRICS CONTEXT
const validate = (value, allValues, props, name) => {
  const limits = {
    ambient_temp: { min: -10, max: 60 },
    humidity: { min: 0, max: 100 },
    ag_ml_in: { min: null, max: null },
    ag_ml_out: { min: null, max: null },
    ag_run_off: { min: 0, max: null },
    ag_ph_in: { min: 0, max: 11 },
    ag_ph_out: { min: 0, max: 11 },
    ag_ph_bag: { min: 0, max: 11 },
    ag_ec_in: { min: 0, max: 8 },
    ag_ec_out: { min: 0, max: 8 },
    ag_ec_bag: { min: 0, max: 8 },
    ag_ec_sum: { min: 0, max: 16 },
    ag_water_cubes: { min: 0, max: 10000 },
    ag_moisture: { min: 0, max: 100 },
    ag_irrigations_per_day: { min: 0, max: null },
    ag_total_run_time: { min: 0, max: 24 * 60 },
    ag_litres_per_day: { min: 0, max: null },
    ag_drain_target: { min: 0, max: 100 },
    ag_ph_target: { min: 0, max: 11 },
    ag_ec_target: { min: 0, max: 8 },
    vpd: { min: 0, max: 42 },
    mildew: { min: 0, max: null },
    growing_hours: { min: null, max: null },
    chilling_hours: { min: null, max: null },
    moisture_content: { min: null, max: null },
    gains: { min: null, max: null },
    gains_neg: { min: null, max: null },
    gains_pos: { min: null, max: null },
    gains_daily: { min: null, max: null },
    vic: { min: null, max: null },
    soil_temp: { min: null, max: null },
    rainfall: { min: null, max: null },
    moisture_10: { min: null, max: null },
    moisture_20: { min: null, max: null },
    moisture_30: { min: null, max: null },
    moisture_40: { min: null, max: null },
    moisture_50: { min: null, max: null },
    moisture_60: { min: null, max: null },
    moisture_70: { min: null, max: null },
    moisture_80: { min: null, max: null },
    moisture_90: { min: null, max: null },
    vic_10: { min: null, max: null },
    vic_20: { min: null, max: null },
    vic_30: { min: null, max: null },
    vic_40: { min: null, max: null },
    vic_50: { min: null, max: null },
    vic_60: { min: null, max: null },
    vic_70: { min: null, max: null },
    vic_80: { min: null, max: null },
    vic_90: { min: null, max: null },
    soil_temp_10: { min: null, max: null },
    soil_temp_20: { min: null, max: null },
    soil_temp_30: { min: null, max: null },
    soil_temp_40: { min: null, max: null },
    soil_temp_50: { min: null, max: null },
    soil_temp_60: { min: null, max: null },
    soil_temp_70: { min: null, max: null },
    soil_temp_80: { min: null, max: null },
    soil_temp_90: { min: null, max: null },
    squeeze: { min: null, max: null },
    run_off: { min: 0, max: 100 },
    ph_in: { min: 0, max: 9 },
    ph_out: { min: 0, max: 9 },
    ph_bag: { min: 0, max: 9 },
    ec_in: { min: 0, max: 4 },
    ec_out: { min: 0, max: 8 },
    ec_bag: { min: 0, max: 4 },
    ec_sum: { min: 0, max: 12 },
  };

  if (value && typeof value === "string") {
    if (value.match(decimalRegex)) {
      const fValue = parseFloat(value);
      if (
        !(
          (limits[name].min === null ||
            fValue >= parseFloat(limits[name].min)) &&
          (limits[name].max === null || fValue <= parseFloat(limits[name].max))
        )
      ) {
        return `Min: ${limits[name].min},  Max: ${limits[name].max}`;
      }
      // max 2 decimal places
      const dp = value.split(".")[1];
      if (dp && dp.length > 2) {
        return "Max 2 decimal places.";
      }
    } else {
      return "Numeric values only.";
    }
  }
  return undefined;
};

const RecordAgronomics = ({
  title,
  timezone,
  handleSubmit,
  error,
  valid,
  pristine,
  submitting,
  isValid,
  isWaiting,
  field,
  mlIn,
  mlOut,
  runOff,
  dispatchChange,
}) => {
  const runOffMultiplier = getRunOffMultFactor(field);

  // Manual mode: enter run-off % directly (hides mL In/Out)
  const [manualRunOff, setManualRunOff] = useState(false);

  useEffect(() => {
    isValid(!pristine && valid && !submitting);
    isWaiting(submitting);
  });

  // Auto-calculate run-off when ml_in and ml_out change (matches PWA formula)
  useEffect(() => {
    if (manualRunOff) return;
    if (mlIn && mlOut) {
      const mlInVal = parseFloat(mlIn);
      const mlOutVal = parseFloat(mlOut);
      if (!isNaN(mlInVal) && !isNaN(mlOutVal) && mlInVal > 0) {
        const result = (mlOutVal / (mlInVal * runOffMultiplier)) * 100;
        dispatchChange("RecordAgronomics", "ag_run_off", result.toFixed(2));
      }
    } else {
      dispatchChange("RecordAgronomics", "ag_run_off", "");
    }
  }, [mlIn, mlOut, runOffMultiplier, manualRunOff, dispatchChange]);

  const allMetrics = useMetricsState();
  const metrics = useMemo(
    () =>
      allMetrics &&
      allMetrics
        .filter((item) => item.module_id === 2 && !item.ref.includes("ec_sum"))
        .sort((a, b) => {
          // Sort by FIELD_ORDER to match PWA
          const aIndex = FIELD_ORDER.indexOf(a.ref);
          const bIndex = FIELD_ORDER.indexOf(b.ref);
          // If not in FIELD_ORDER, put at end
          const aPos = aIndex === -1 ? FIELD_ORDER.length : aIndex;
          const bPos = bIndex === -1 ? FIELD_ORDER.length : bIndex;
          return aPos - bPos;
        }),
    [allMetrics]
  );

  // Group metrics by category
  const groupedMetrics = useMemo(() => {
    const groups = {};
    Object.entries(FIELD_GROUPS).forEach(([groupName, refs]) => {
      groups[groupName] = refs
        .map((ref) => metrics.find((m) => m.ref === ref))
        .filter(Boolean);
    });
    return groups;
  }, [metrics]);

  const renderField = (metric, isFirst) => {
    const isMlIn = metric.ref === "ag_ml_in";

    let label = metric.short_name;
    if (isMlIn) {
      label = `${metric.short_name} (x${runOffMultiplier.toFixed(2)})`;
    }

    return (
      <Field
        key={`agronomics-input-${metric.ref}`}
        component={TextInput}
        name={metric.ref}
        id={metric.ref}
        type={metric.ref}
        autoFocus={isFirst}
        label={label}
        validate={validate}
      />
    );
  };

  const renderRunOffGroup = (groupMetrics) => {
    const mlFields = groupMetrics.filter((m) => m.ref !== "ag_run_off");
    const runOffField = groupMetrics.find((m) => m.ref === "ag_run_off");
    const isAutoCalc = !manualRunOff && mlIn && mlOut;

    return (
      <Grid container spacing={2}>
        {!manualRunOff &&
          mlFields.map((metric) => (
            <Grid item xs={12} sm={6} key={metric.ref}>
              {renderField(metric, false)}
            </Grid>
          ))}
        {runOffField && (
          <Grid item xs={12} sm={manualRunOff ? 6 : 12}>
            <Field
              component={TextInput}
              name="ag_run_off"
              id="ag_run_off"
              type="ag_run_off"
              label={
                isAutoCalc
                  ? `${runOffField.short_name} % (auto)`
                  : `${runOffField.short_name} %`
              }
              validate={validate}
              disabled={!manualRunOff}
            />
          </Grid>
        )}
        {!manualRunOff && !runOff && (
          <Grid item xs={12}>
            <Button
              size="small"
              color="primary"
              onClick={() => setManualRunOff(true)}
            >
              Enter manually
            </Button>
          </Grid>
        )}
      </Grid>
    );
  };


  let isFirstField = true;

  return (
    <div className="agronomics-form">
      {title && (
        <Typography variant="h3" gutterBottom>
          {title}
        </Typography>
      )}
      <form noValidate onSubmit={handleSubmit}>
        <Field
          component={DateTimeInput}
          name="timestamp"
          id="timestamp"
          label="Timestamp"
          timezone={timezone}
        />

        {Object.entries(groupedMetrics).map(([groupName, groupMetrics]) => {
          if (!groupMetrics.length) return null;

          const isRunOffGroup = groupName === "Run-off";

          // pH and EC: 3 related measurements in a row
          const getColumnWidth = (metricRef) => {
            if (metricRef.includes("_ph_") || metricRef.includes("_ec_")) {
              return 4;
            }
            return 6;
          };

          return (
            <Box
              key={groupName}
              mt={3}
              pt={2}
              style={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}
            >
              <Typography
                variant="overline"
                style={{ fontWeight: 600, display: "block", marginBottom: -16 }}
              >
                {groupName}
              </Typography>
              {isRunOffGroup ? (
                renderRunOffGroup(groupMetrics)
              ) : (
                <Grid container spacing={2}>
                  {groupMetrics.map((metric) => {
                    const field = renderField(metric, isFirstField);
                    isFirstField = false;
                    return (
                      <Grid item xs={12} sm={getColumnWidth(metric.ref)} key={metric.ref}>
                        {field}
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          );
        })}

        <Box
          mt={3}
          pt={2}
          style={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}
        >
          <Typography
            variant="overline"
            style={{ fontWeight: 600, display: "block", marginBottom: -16 }}
          >
            Notes
          </Typography>
          <Field component={TextInput} name="notes" id="notes" label="Notes" />
        </Box>

        <Typography variant="body1" gutterBottom color="error">
          <b>{error}</b>
        </Typography>
      </form>
    </div>
  );
};

const selector = formValueSelector("RecordAgronomics");

const mapStateToProps = (state) => ({
  mlIn: selector(state, "ag_ml_in"),
  mlOut: selector(state, "ag_ml_out"),
  runOff: selector(state, "ag_run_off"),
});

const mapDispatchToProps = {
  dispatchChange: change,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: "RecordAgronomics" })(RecordAgronomics));
